<template>
  <v-form
    ref="form"
    v-model="isFormValid"
  >
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.name") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">{{ formValues.name || "-" }} </template>
        <v-text-field
          v-else
          clearable
          @input="handleInput"
          :rules="rules.name"
          type="text"
          :label="$t('commons.name')"
          v-model="formValues.name"
          dense
          outlined
        />
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.date.start_date") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">{{ formatDatetime(formValues.start_date) }} </template>
        <DatetimePicker
          v-else
          @input="handleInput"
          v-model="formValues.start_date"
          :textFieldProps="{
            placeholder: $t('commons.date.start_date'),
            rules: rules.start_date
          }"
        />
        <!-- <DatetimePicker
          v-else
          @input="handleInput"
          v-model="formValues.start_date"
          :max="formValues.end_date"
          :textFieldProps="{
            placeholder: $t('commons.date.start_date'),
            rules: rules.start_date
          }"
        /> -->
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.date.end_date") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">{{ formatDatetime(formValues.end_date) }} </template>
        <DatetimePicker
          v-else
          @input="handleInput"
          v-model="formValues.end_date"
          :min="formValues.start_date"
          :textFieldProps="{
            placeholder: $t('commons.date.end_date'),
            rules: rules.end_date
          }"
        />
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $t("commons.status.name") }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <template v-if="readOnly">{{ formValues.status || "-" }} </template>
        <StatusInput
          v-else
          v-model="formValues.status"
          :rules="rules.status"
          @input="handleInput"
        />
      </v-col>
    </v-row>
    <v-row
      class="mt-0 d-flex align-baseline"
      v-if="!readOnly"
    >
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $tc("categories.notification.name", 1) }}
      </v-col>
      <v-col class="pb-0 mb-0">
        <v-checkbox
          :disabled="readOnly"
          v-model="formValues.notify_oncreated"
          @change="handleNotifyOnCreatedChange"
        >
          <template v-slot:label>
            <div>{{ $t("categories.inspection.send_notifications") }}</div>
          </template>
        </v-checkbox>
      </v-col>
      <v-col class="pb-0 mb-0">
        <v-checkbox
          :disabled="readOnly"
          v-model="formValues.notify_team_oncreated"
          @change="handleNotifyTeamOnCreatedChange"
        >
          <template v-slot:label>
            <div>{{ $t("categories.inspection.send_select_team_notifications") }}</div>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>
    <v-row
      class="mt-0 d-flex align-baseline"
      v-else
    >
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      >
        {{ $tc("categories.notification.name", 1) }}
      </v-col>
      <v-col
        class="pb-0 mb-0"
        :cols="readOnly ? 6 : 6"
      >
        <v-checkbox
          :disabled="readOnly"
          v-model="formValues.notify_oncreated"
          @change="handleNotifyOnCreatedChange"
        >
          <template v-slot:label>
            <div>{{ $t("categories.inspection.send_notifications") }}</div>
          </template>
        </v-checkbox>
        <v-checkbox
          :disabled="readOnly"
          v-model="formValues.notify_team_oncreated"
          @change="handleNotifyTeamOnCreatedChange"
        >
          <template v-slot:label>
            <div>{{ $t("categories.inspection.send_select_team_notifications") }}</div>
          </template>
        </v-checkbox>
      </v-col>
    </v-row>
    <v-row class="mt-0 d-flex align-baseline">
      <v-col
        cols="12"
        md="4"
        class="primary--text font-weight-bold text-h6"
      ></v-col>
      <v-col
        v-if="formValues.notify_team_oncreated && !readOnly"
        cols="12"
        md="8"
      >
        <v-select
          :disabled="readOnly"
          v-model="formValues.users"
          :items="users"
          item-text="email"
          item-value="id"
          :label="$t('commons.send_to')"
          multiple
          chips
          attach
          outlined
          @input="handleInput"
        ></v-select>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import _ from "lodash"
import { TeamService } from "@/services"
import { mkInspection } from "@/utils/datamodels"
import { toDatetime } from "@/utils/datetime"
import { required, dateCompare } from "@/utils/validator"

export default {
  name: "InspectionForm",
  components: {
    DatetimePicker: () => import("@/components/inputs/DatetimePicker.vue"),
    StatusInput: () => import("@/components/inputs/StatusInput.vue")
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    validateCounter: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isFormValid: true,
      // rules: {
      //   name: [required],
      //   status: [required],
      //   start_date: [required],
      //   end_date: [required, dateCompare(this.formValues.start_date, this.formValues.end_date)]
      // },
      formValues: mkInspection(),
      users: []
    }
  },
  computed: {
    rules() {
      return {
        name: [required],
        status: [required],
        start_date: [required],
        end_date: [
          required,
          () => dateCompare(this.formValues.start_date, this.formValues.end_date)
        ]
      }
    },
    projectId() {
      return this.$store.state.app.project.id
    }
  },
  created() {
    this.fetchFieldTypes()
  },
  methods: {
    formatDatetime(value) {
      return value ? toDatetime(value) : "-"
    },
    handleInput() {
      this.$emit("input", this.formValues)
    },
    handleNotifyOnCreatedChange() {
      if (this.formValues.notify_oncreated) {
        this.formValues.notify_team_oncreated = false
        this.formValues.users = []
      }
      this.handleInput()
    },
    handleNotifyTeamOnCreatedChange() {
      if (this.formValues.notify_team_oncreated) {
        this.formValues.notify_oncreated = false
      } else {
        this.formValues.users = []
      }
      this.handleInput()
    },
    fetchFieldTypes() {
      TeamService.getTeam(this, this.projectId).then(response => {
        this.users = response
      })
    }
  },
  mounted() {
    if (this.value) this.formValues = _.cloneDeep(this.value)
  },
  watch: {
    validateCounter(newValue) {
      this.$refs["form"].validate()
    },
    isFormValid(newValue) {
      this.$emit("validated", newValue)
    },
    value(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        this.formValues = _.cloneDeep(this.value)
      }
    }
  }
}
</script>
